import { get, post, put, patch, del, API_URL, api } from "./client";

export const authApi = {
  login: (username, password) => post("/auth/login", { username, password }, { silent: true }),
  me: () => get("/auth/me", null, { silent: true }),
  loginContent: () => get("/public/login-content", null, { silent: true }),
};

export const loginContentApi = {
  get: () => get("/admin/login-content"),
  save: (body) => put("/admin/login-content", body),
};

export const callExportsApi = {
  preview: (body) => post("/admin/call-exports/preview", body),
  create: (body) => post("/admin/call-exports", body),
  list: () => get("/admin/call-exports", null, { silent: true }),
  url: (id) => get(`/admin/call-exports/${id}/url`),
  remove: (id) => del(`/admin/call-exports/${id}`),
  metadataCsv: (body) => api.post("/admin/call-exports/metadata.csv", body, { responseType: "blob" }).then((r) => r.data),
};

// Rychlé volání si načítá detail dalšího kontaktu na pozadí – „Další“ je pak okamžité
const detailPrefetch = new Map();

export const contactsApi = {
  list: (params) => get("/contacts", params),
  autocomplete: (q) => get("/contacts/autocomplete", { q }),
  duplicates: (params) => get("/contacts/duplicates", params),
  get: (id) => get(`/contacts/${id}`),
  detail: (id) => { const p = detailPrefetch.get(id); if (p && Date.now() - p.at < 60000) { detailPrefetch.delete(id); return p.promise; } return get(`/contacts/${id}/detail`); },
  prefetchDetail: (id) => { if (!id || detailPrefetch.has(id)) return; detailPrefetch.set(id, { at: Date.now(), promise: get(`/contacts/${id}/detail`).catch(() => { detailPrefetch.delete(id); return null; }) }); },
  seedDetail: (id, detail) => { if (id && detail) detailPrefetch.set(id, { at: Date.now(), promise: Promise.resolve(detail) }); },
  nextCall: (params) => get("/contacts/next-call", { ...params, with_detail: true }).then((r) => { if (r?.contact && r.detail) { contactsApi.seedDetail(r.contact.id, r.detail); delete r.detail; } return r; }),
  callPresets: () => get("/contacts/call-presets"),
  savePreset: (d) => post("/contacts/call-presets", d),
  deletePreset: (id) => del(`/contacts/call-presets/${id}`),
  create: (data) => post("/contacts", data),
  update: (id, data) => put(`/contacts/${id}`, data),
  remove: (id) => del(`/contacts/${id}`),
  merge: (source_id, target_id) => post("/contacts/merge", { source_id, target_id }),
  findPeople: (id, url) => post(`/contacts/${id}/find-people`, { url }),
  addPeople: (id, people) => post(`/contacts/${id}/add-people`, { people }),
  markFaulty: (data) => post("/contacts/faulty", data),
  faulty: (resolved = false) => get("/contacts/faulty", { resolved }),
  resolveFaulty: (id) => post(`/contacts/faulty/${id}/resolve`),
  importFile: (file, skip_duplicates = true) => {
    const fd = new FormData();
    fd.append("file", file);
    return post(`/contacts/import?skip_duplicates=${skip_duplicates}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
};

export const projectsApi = {
  summary: (id, force) => get(`/projects/${id}/summary`, force ? { force: true } : undefined),
  types: () => get("/project-types"),
  createType: (data) => post("/project-types", data),
  updateType: (id, data) => put(`/project-types/${id}`, data),
  deleteType: (id) => del(`/project-types/${id}`),
  copyConfig: (id, from_id, what) => post(`/project-types/${id}/copy-config`, { from_id, what }),
  create: (data) => post("/projects", data),
  update: (id, data) => put(`/projects/${id}`, data),
  changeStatus: (id, data) => put(`/projects/${id}/status`, data),
  remove: (id) => del(`/projects/${id}`),
  pipeline: (typeId, owner_id) => get(`/projects/pipeline/${typeId}`, { owner_id }),
};

export const tasksApi = {
  list: (params) => get("/tasks", params),
  get: (id) => get(`/tasks/${id}`),
  counts: () => get("/tasks/counts"),
  create: (data) => post("/tasks", data),
  update: (id, data) => put(`/tasks/${id}`, data),
  complete: (id, data) => post(`/tasks/${id}/complete`, data),
  delegate: (id, to_user_id, note) => post(`/tasks/${id}/delegate`, { to_user_id, note }),
  bulk: (data) => post("/tasks/bulk", data),
  completeForProject: (projectId, keepTaskId) => post(`/tasks/complete-for-project/${projectId}${keepTaskId ? `?keep_task_id=${keepTaskId}` : ""}`),
  remove: (id) => del(`/tasks/${id}`),
};

export const usersApi = {
  list: (params) => get("/users", params),
  create: (data) => post("/users", data),
  update: (id, data) => put(`/users/${id}`, data),
  remove: (id) => del(`/users/${id}`),
  teams: () => get("/users/teams"),
  createTeam: (data) => post("/users/teams", data),
  deleteTeam: (id) => del(`/users/teams/${id}`),
  upsertEmailAccount: (userId, data) => put(`/users/${userId}/email-accounts`, data),
  deleteEmailAccount: (userId, accId) => del(`/users/${userId}/email-accounts/${accId}`),
};

export const configApi = {
  settings: () => get("/settings"),
  updateSettings: (d) => put("/settings", d),
  customFields: () => get("/custom-fields"),
  createCustomField: (d) => post("/custom-fields", d),
  updateCustomField: (id, d) => put(`/custom-fields/${id}`, d),
  deleteCustomField: (id) => del(`/custom-fields/${id}`),
  tags: () => get("/tags"),
  createTag: (d) => post("/tags", d),
  updateTag: (id, d) => put(`/tags/${id}`, d),
  deleteTag: (id) => del(`/tags/${id}`),
  flagTypes: () => get("/flag-types"),
  createFlagType: (d) => post("/flag-types", d),
  updateFlagType: (id, d) => put(`/flag-types/${id}`, d),
  deleteFlagType: (id) => del(`/flag-types/${id}`),
  addFlag: (d) => post("/flags", d),
  removeFlag: (id) => del(`/flags/${id}`),
};

export const quickActionsApi = {
  list: (scope) => get("/quick-actions", { scope }),
  listAll: () => get("/quick-actions", { all: true }),
  create: (d) => post("/quick-actions", d),
  update: (id, d) => put(`/quick-actions/${id}`, d),
  remove: (id) => del(`/quick-actions/${id}`),
  execute: (id, d) => post(`/quick-actions/${id}/execute`, d),
};

export const importV1Api = {
  files: () => get("/admin/import-v1/files"),
  upload: (file, chunk, total, blob) => { const fd = new FormData(); fd.append("file", blob || file, file.name); fd.append("chunk", chunk); fd.append("total", total); return post("/admin/import-v1/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); },
  remove: (col) => del(`/admin/import-v1/files/${col}`),
  status: () => get("/admin/import-v1/status", null, { silent: true }),
  run: (clear_demo) => post(`/admin/import-v1/run?clear_demo=${clear_demo}`),
  repairOpenTasks: () => post("/admin/import-v1/repair-open-tasks"),
  backfillEmailProjects: () => post("/emails/backfill-projects"),
};

export const leadsApi = {
  sources: () => get("/lead-sources"),
  createSource: (d) => post("/lead-sources", d),
  updateSource: (id, d) => put(`/lead-sources/${id}`, d),
  deleteSource: (id) => del(`/lead-sources/${id}`),
  sync: (id) => post(`/lead-sources/${id}/sync`),
  webinars: () => get("/webinars"),
  createWebinar: (d) => post("/webinars", d),
  updateWebinar: (id, d) => put(`/webinars/${id}`, d),
  deleteWebinar: (id) => del(`/webinars/${id}`),
  attendance: (id, emails) => post(`/webinars/${id}/attendance`, { emails, mark_others_absent: true }),
  list: (params) => get("/leads", params),
  create: (d) => post("/leads", d),
  update: (id, d) => put(`/leads/${id}`, d),
  claim: (id) => post(`/leads/${id}/claim`),
  linkContact: (id, contact_id) => post(`/leads/${id}/link`, { contact_id }),
  addPerson: (id) => post(`/leads/${id}/add-person`),
  rematch: () => post("/leads/rematch"),
  rebuildWebinarFlags: () => post("/leads/webinar-flags/rebuild"),
  job: (id) => get(`/jobs/${id}`),
  summary: (user_id) => get("/leads/summary", user_id ? { user_id } : undefined),
  release: (id) => post(`/leads/${id}/release`),
  deals: (params) => get("/deals", params),
  createDeal: (d) => post("/deals", d),
  deleteDeal: (id) => del(`/deals/${id}`),
  bulkDeleteDeals: (ids) => post("/deals/bulk-delete", { ids }),
  bulkLeads: (ids, action) => post("/leads/bulk", { ids, action }),
};

export const emailApi = {
  accounts: (all) => get("/email-accounts", all ? { all: true } : undefined),
  createAccount: (d) => post("/email-accounts", d),
  updateAccount: (id, d) => put(`/email-accounts/${id}`, d),
  deleteAccount: (id) => del(`/email-accounts/${id}`),
  testAccount: (id) => post(`/email-accounts/${id}/test`),
  resendDomains: () => get("/email-accounts/resend-domains"),
  syncMine: () => post("/emails/sync"),
  syncAccount: (id) => post(`/email-accounts/${id}/sync`),
  fullSync: (id) => post(`/email-accounts/${id}/full-sync`),
  templates: (all) => get("/email-templates", all ? { all: true } : undefined),
  createTemplate: (d) => post("/email-templates", d),
  updateTemplate: (id, d) => put(`/email-templates/${id}`, d),
  deleteTemplate: (id) => del(`/email-templates/${id}`),
  render: (params) => get("/emails/render", params),
  send: (d) => post("/emails/send", d),
  list: (params) => get("/emails", params),
  get: (id) => get(`/emails/${id}`),
  mark: (id, params) => post(`/emails/${id}/mark?${new URLSearchParams(params).toString()}`),
  assign: (id, d) => post(`/emails/${id}/assign`, d),
  bulk: (ids, d) => post("/emails/bulk", { ids, ...d }),
  bulkDelete: (ids) => post("/emails/bulk-delete", { ids }),
  remove: (id) => del(`/emails/${id}`),
  counts: (params) => get("/emails/counts", params),
  cleanTemplates: () => post("/email-templates/clean"),
};

export const analyticsApi = {
  calls: (params) => get("/analytics/calls", params),
  today: () => get("/analytics/today"),
  inactive: (params) => get("/analytics/inactive-projects", params),
};

export const qualityApi = {
  catalog: () => get("/quality/catalog"),
  overview: () => get("/quality/overview"),
  rules: () => get("/quality/rules"),
  createRule: (d) => post("/quality/rules", d),
  updateRule: (id, d) => put(`/quality/rules/${id}`, d),
  deleteRule: (id) => del(`/quality/rules/${id}`),
  seedDefaults: () => post("/quality/rules/seed-defaults"),
  items: (id, params) => get(`/quality/rules/${id}/items`, params),
  ignore: (rule_id, entity_id, note) => post("/quality/ignore", { rule_id, entity_id, note }),
  unignore: (rule_id, entity_id) => del(`/quality/ignore/${rule_id}/${entity_id}`),
};

export const telephonyApi = {
  settings: () => get("/telephony/settings"),
  saveSettings: (d) => put("/telephony/settings", d),
  test: () => post("/telephony/test"),
  subscribe: () => post("/telephony/subscribe"),
  syncCdr: (hours) => post(`/telephony/sync-cdr?hours=${hours || 24}`),
  syncStatus: () => get("/telephony/sync-status"),
  dial: (d) => post("/telephony/dial", d),
  incoming: () => get("/telephony/incoming", null, { silent: true }),
  seen: (id) => post(`/telephony/incoming/${id}/seen`),
  analyze: (callId) => post(`/telephony/calls/${callId}/analyze`),
  bulkStatus: () => get("/telephony/transcribe-all"),
  bulkStart: (retry) => post(`/telephony/transcribe-all?retry_errors=${retry ? "true" : "false"}`),
  bulkStop: () => post("/telephony/transcribe-all/stop"),
  archive: (callId) => post(`/telephony/calls/${callId}/archive-recording`),
  calls: (params) => get("/telephony/calls", params),
  call: (id) => get(`/telephony/calls/${id}`),
  assign: (id, d) => post(`/telephony/calls/${id}/assign`, d),
  recordingUrl: (id, token) => `${API_URL}/telephony/calls/${id}/recording?token=${token}`,
  search: (params) => get("/telephony/search", params),
  apiKeys: () => get("/telephony/api-keys"),
  createApiKey: (name) => post("/telephony/api-keys", { name }),
  revokeApiKey: (id) => del(`/telephony/api-keys/${id}`),
  storage: () => get("/telephony/storage"),
  r2Test: () => post("/telephony/r2-test"),
  retentionRun: () => post("/telephony/retention-run"),
};

export const activityApi = {
  createNote: (d) => post("/notes", d),
  updateNote: (id, d) => put(`/notes/${id}`, d),
  deleteNote: (id) => del(`/notes/${id}`),
  logCall: (d) => post("/calls", d),
  updateCall: (id, d) => patch(`/calls/${id}`, d),
  notifications: (unread_only = false) => get("/notifications", { unread_only }, { silent: true }),
  markRead: (ids) => post("/notifications/read", { ids }),
  audit: (params) => get("/admin/audit", params),
  oversight: (days) => get("/admin/oversight", { days }),
  reassignInactive: (to_user_id) => post("/admin/oversight/reassign-inactive", { to_user_id: to_user_id || null }),
  dashboard: () => get("/dashboard"),
};
